import React, {useState, useEffect, useRef, useContext, useId} from "react";
import CircularLoader from "../common/CircularLoader";
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from "./Button";
import useFocusWithDelay, {FOCUS_SELECTOR_ARR} from "../hooks/useFocusWithDelay";
import IconButton from "./IconButton";
import {Fullscreen, FullscreenExit} from "@mui/icons-material";
import {ContentContext} from "./ContentContext";
import {NavigationContext} from "./NavigationContext";
import TooltipMUI from "@mui/material/Tooltip";

import Paper from '@mui/material/Paper';
import Draggable from 'react-draggable';

function PaperComponent(props) {
    return (
        <Draggable
            handle="#draggable-dialog-title" // This selector must match the ID in your title div below
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} />
        </Draggable>
    );
}

const GenericDialog = props => {
    const {
            title,
            children,
            errorMessage,
            className = '',
            onClose,
            buttons = [],
            open = false,
            isLoading = false,
            icon,
            fullWidth = false,
            fullscreen = false,
            fullScreenCallback,
            actionStyle,
            contentStyle,
            dialogWidth,
            allowFullScreen = false,
            ...other
        } = props,
        {setReactDialogOpen} = useContext(NavigationContext),
        {dialogMediaObject} = useContext(ContentContext),
        [isButtonsEnabled, setButtonsEnabled] = useState(true),
        [isProcessing, setProcessing] = useState(false),
        [isDialogFullScreen, setDialogFullScreen] = useState(fullscreen),
        [isDialogFullWidth, setDialogFullWidth] = useState(fullWidth),
        [dialogFullscreenLabel, setDialogFullscreenLabel] = useState(''),
        dialogRef = useRef();

    const titleId = useId();
    const contentId = `${titleId}-content`;

    // Safe access to global functions (avoid ReferenceError if not defined)
    const grf = (typeof window !== "undefined") ? window.globalReactFunctions : undefined;

    useEffect(() => {
        setButtonsEnabled(open);
        setProcessing(false);
        setReactDialogOpen(open);
    }, [open, setReactDialogOpen]);

    useEffect(() => {
        if (!isButtonsEnabled || !dialogRef.current) return;

        const focusableElements = dialogRef.current.querySelectorAll(FOCUS_SELECTOR_ARR.join(','));
        if (focusableElements.length === 0) return;

        const firstFocusableElement =
            Array.from(focusableElements).find(el => el.offsetParent !== null && !el.disabled);

        if (firstFocusableElement) {
            useFocusWithDelay(firstFocusableElement);
        }
    }, [isButtonsEnabled, isLoading]);

    useEffect(() => {
        if (allowFullScreen && dialogMediaObject) {
            switch (dialogMediaObject?.mediaType) {
                case 'WIDGET':
                    widgetResize();
                    break;
                case 'SCORCHER':
                    if (grf?.resizeScorcherCanvas && dialogMediaObject?.mediaType === 'SCORCHER') {
                        grf.resizeScorcherCanvas();
                    }
                    break;
                default:
                    break;
            }
        }
        setDialogFullscreenLabel(`${isDialogFullScreen ? `Exit` : 'Enter'} fullscreen`);
    }, [isDialogFullScreen, allowFullScreen, dialogMediaObject]); // grf is stable enough as read-only global

    const handleClose = () => {
        if (onClose && typeof onClose === 'function') onClose();
        setReactDialogOpen(false);
    }

    const widgetResize = () => {
        if (grf?.resizeWidgetCanvas && dialogMediaObject?.mediaSubType === 'WIDGET_JAVASCRIPT') {
            grf.resizeWidgetCanvas();
        }
        if (grf?.resizeWidgetSwiffy && dialogMediaObject?.mediaSubType === 'WIDGET_SWIFFY') {
            grf.resizeWidgetSwiffy();
        }
    }

    const toggleFullScreen = () => {
        const next = !isDialogFullScreen;
        setDialogFullWidth(!isDialogFullWidth);
        setDialogFullScreen(next);
        if (fullScreenCallback && typeof fullScreenCallback === 'function') {
            fullScreenCallback(next);
        }
    }

    return (
        <Dialog
            disableEnforceFocus
            ref={dialogRef}
            className={className+' ch-dialog'}
            open={open}
            fullWidth={isDialogFullWidth}
            fullScreen={isDialogFullScreen}
            maxWidth={isDialogFullScreen ? 'xl' : (dialogWidth && dialogWidth > 900) ? 'lg' : 'md'}
            aria-labelledby={title ? titleId : undefined}
            aria-describedby={contentId}
            aria-modal="true"
            onClose={handleClose}
            PaperComponent={isDialogFullScreen ? undefined : PaperComponent}
            {...((dialogWidth && !isDialogFullScreen)
                ? {
                    PaperProps: {
                        sx: {
                            flexBasis: `${dialogWidth}px`,
                        },
                    },
                }
                : {})}
            {...other}
        >
            {title && (
                <div
                    className={'ch-dialog-title'}
                    id="draggable-dialog-title"
                    style={{ cursor: isDialogFullScreen ? 'default' : 'move' }}
                >
                    <div className={'ch-dialog-title-text'}>
                        {icon && <>{icon}</>}
                        <h1 id={titleId}>{title}</h1>
                    </div>
                    {allowFullScreen &&
                        <TooltipMUI placement={'left'} title={dialogFullscreenLabel}>
                            <IconButton onClick={toggleFullScreen} aria-label={dialogFullscreenLabel}>
                                {isDialogFullScreen ? <FullscreenExit /> : <Fullscreen />}
                            </IconButton>
                        </TooltipMUI>
                    }
                </div>
            )}
            <DialogContent
                id={contentId}
                className={`ch-dialog-content${isDialogFullScreen ? ` fullscreen` : ``}`}
                style={contentStyle ? contentStyle : {}}
            >
                {errorMessage &&
                    <div className={'ch-error-text'}>{errorMessage}</div>
                }
                {isLoading &&
                    <CircularLoader pageCentered={false}/>
                }
                {!isLoading &&
                    <>
                        {children}
                    </>
                }
            </DialogContent>
            {buttons.length > 0 &&
                <DialogActions className={'ch-dialog-actions'} style={actionStyle ? actionStyle : {}}>
                    <CircularLoader pageCentered={false} lightStyle={false} visible={isProcessing}/>
                    {buttons.map((button, index) => (
                        !button.hidden &&
                        <Button
                            {...button.extraProps}
                            disabled={button.disabled || !isButtonsEnabled}
                            key={index}
                            onClick={async () => {
                                if (!isButtonsEnabled) return;

                                if (button.callback && typeof button.callback === 'function') {
                                    setButtonsEnabled(false);
                                    setProcessing(true);
                                    try {
                                        await Promise.resolve(button.callback());
                                    } catch (error) {
                                        console.error('Error', error);
                                    } finally {
                                        setProcessing(false);
                                        setButtonsEnabled(true);
                                    }
                                } else {
                                    handleClose();
                                }
                            }}
                        >
                            {button.name}
                        </Button>
                    ))}
                </DialogActions>
            }
        </Dialog>
    )
}

export default GenericDialog;